import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  Alert,
  Grid,
  Paper,
  Divider,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { PatternFormProps, PatternFormData, PatternValidationErrors, PATTERN_CONSTANTS } from '../../types/pattern';
import { SynonymManager } from '../SynonymManager';

const PatternForm: React.FC<PatternFormProps> = ({
  pattern,
  elementTypes,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState<PatternFormData>({
    description: '',
    element_type_id: 0,
    matching_string: '',
    absolute_positioning: undefined,
    is_logo: false,
    status: true,
    front_svg_file: undefined,
    side_svg_file: undefined,
  });
  const [errors, setErrors] = useState<PatternValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [svgPreviews, setSvgPreviews] = useState<{
    front?: string;
    side?: string;
  }>({});

  // Initialize form data when pattern prop changes
  useEffect(() => {
    if (pattern) {
      setFormData({
        description: pattern.description,
        element_type_id: pattern.element_type_id,
        matching_string: pattern.matching_string || '',
        absolute_positioning: pattern.absolute_positioning ?? undefined,
        absolute_positioning_front: pattern.absolute_positioning_front ?? undefined,
        absolute_positioning_side: pattern.absolute_positioning_side ?? undefined,
        is_logo: (pattern as any).is_logo ?? false,
        status: pattern.status,
        front_svg_file: undefined,
        side_svg_file: undefined,
      });
    } else {
      setFormData({
        description: '',
        element_type_id: elementTypes.length > 0 ? elementTypes[0].id : 0,
        matching_string: '',
        absolute_positioning: undefined,
        absolute_positioning_front: undefined,
        absolute_positioning_side: undefined,
        is_logo: false,
        status: true,
        front_svg_file: undefined,
        side_svg_file: undefined,
      });
    }
  }, [pattern, elementTypes]);

  const validateField = (field: keyof PatternFormData, value: any): string | undefined => {
    switch (field) {
      case 'description':
        if (!value || value.trim().length === 0) {
          return 'Description is required';
        }
        if (value.length > PATTERN_CONSTANTS.MAX_DESCRIPTION_LENGTH) {
          return `Description must be less than ${PATTERN_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters`;
        }
        return undefined;

      case 'element_type_id':
        if (!value || value === 0) {
          return 'Element type is required';
        }
        return undefined;

      case 'matching_string':
        if (value && value.length > PATTERN_CONSTANTS.MAX_MATCHING_STRING_LENGTH) {
          return `Matching string must be less than ${PATTERN_CONSTANTS.MAX_MATCHING_STRING_LENGTH} characters`;
        }
        return undefined;

      default:
        return undefined;
    }
  };

  const validateForm = (fileErrors: PatternValidationErrors = {}): boolean => {
    const newErrors: PatternValidationErrors = { ...fileErrors };

    Object.keys(formData).forEach((key) => {
      const field = key as keyof PatternFormData;
      const error = validateField(field, formData[field]);
      if (error) {
        (newErrors as any)[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field: keyof PatternFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (field in errors && (errors as any)[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleFieldBlur = (field: keyof PatternFormData) => {
    setTouched(prev => ({
      ...prev,
      [field]: true,
    }));

    const error = validateField(field, formData[field]);
    setErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  // SVG file handling
  const handleSvgFileChange = (view: 'front' | 'side', file: File | null) => {
    const fieldName = view === 'front' ? 'front_svg_file' : 'side_svg_file';

    setFormData(prev => ({
      ...prev,
      [fieldName]: file || undefined,
    }));

    // Generate preview
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setSvgPreviews(prev => ({
          ...prev,
          [view]: content,
        }));
      };
      reader.readAsText(file);
    } else {
      setSvgPreviews(prev => ({
        ...prev,
        [view]: undefined,
      }));
    }
  };

  const validateSvgFile = (file: File): string | null => {
    // Check file type
    if (!file.type.includes('svg') && !file.name.toLowerCase().endsWith('.svg')) {
      return 'File must be an SVG file';
    }

    // Check file size (max 1MB — list endpoint returns SVGs inline, Lambda 6MB response cap)
    if (file.size > 1024 * 1024) {
      return 'File size must be less than 1MB — optimise the SVG (e.g. remove embedded images, run it through SVGO)';
    }

    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => ({
      ...acc,
      [key]: true,
    }), {});
    setTouched(allTouched);

    // Validate SVG files (passed into validateForm so its setErrors doesn't wipe them)
    const fileErrors: PatternValidationErrors = {};
    const frontError = formData.front_svg_file && validateSvgFile(formData.front_svg_file);
    const sideError = formData.side_svg_file && validateSvgFile(formData.side_svg_file);
    if (frontError) fileErrors.front_svg_file = frontError;
    if (sideError) fileErrors.side_svg_file = sideError;

    if (validateForm(fileErrors)) {
      onSubmit(formData);
    }
  };

  const selectedElementType = elementTypes.find(et => et.id === formData.element_type_id);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Pattern Description"
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  onBlur={() => handleFieldBlur('description')}
                  error={touched.description && !!errors.description}
                  helperText={
                    touched.description && errors.description
                      ? errors.description
                      : 'Enter a descriptive name for the pattern (e.g., "Hoops", "Stripes", "Checked")'
                  }
                  required
                  inputProps={{
                    maxLength: PATTERN_CONSTANTS.MAX_DESCRIPTION_LENGTH,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={touched.element_type_id && !!errors.element_type_id}>
                  <InputLabel>Element Type</InputLabel>
                  <Select
                    value={formData.element_type_id}
                    onChange={(e) => handleFieldChange('element_type_id', e.target.value)}
                    onBlur={() => handleFieldBlur('element_type_id')}
                    label="Element Type"
                    required
                  >
                    {elementTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.element_type_id && errors.element_type_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.element_type_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status}
                      onChange={(e) => handleFieldChange('status', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            {/* Logo toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.is_logo}
                    onChange={(e) => handleFieldChange('is_logo', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Is Logo Pattern</Typography>
                    <Typography variant="caption" color="text.secondary">
                      When enabled, the generator will preserve the pattern's full color palette and apply logo-safe positioning/clipping.
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            </Grid>
          </Paper>
        </Grid>

        {/* Advanced Settings */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Advanced Settings
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Matching String"
                  value={formData.matching_string}
                  onChange={(e) => handleFieldChange('matching_string', e.target.value)}
                  onBlur={() => handleFieldBlur('matching_string')}
                  error={touched.matching_string && !!errors.matching_string}
                  helperText={
                    touched.matching_string && errors.matching_string
                      ? errors.matching_string
                      : 'Optional: Natural language terms that should match this pattern (e.g., "hooped", "striped", "banded")'
                  }
                  multiline
                  rows={2}
                  inputProps={{
                    maxLength: PATTERN_CONSTANTS.MAX_MATCHING_STRING_LENGTH,
                  }}
                />
              </Grid>

              {/* Absolute positioning tri-state selector */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="abs-pos-global-label">Positioning (Global)</InputLabel>
                  <Select
                    labelId="abs-pos-global-label"
                    label="Positioning (Global)"
                    value={
                      formData.absolute_positioning === true ? 'absolute' :
                      formData.absolute_positioning === false ? 'relative' : 'auto'
                    }
                    onChange={(e) => {
                      const val = e.target.value as 'auto' | 'absolute' | 'relative';
                      let next: boolean | null | undefined = undefined;
                      if (val === 'absolute') next = true;
                      else if (val === 'relative') next = false;
                      else next = undefined; // auto

                      // When user selects a global value (absolute/relative), default both views unless user later changes them
                      handleFieldChange('absolute_positioning', next);
                      if (val === 'absolute' || val === 'relative') {
                        handleFieldChange('absolute_positioning_front', next);
                        handleFieldChange('absolute_positioning_side', next);
                      }
                    }}
                  >
                    <MenuItem value="auto">Auto</MenuItem>
                    <MenuItem value="absolute">Absolute</MenuItem>
                    <MenuItem value="relative">Relative</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="abs-pos-front-label">Front Positioning</InputLabel>
                  <Select
                    labelId="abs-pos-front-label"
                    label="Front Positioning"
                    value={
                      formData.absolute_positioning_front === true ? 'absolute' :
                      formData.absolute_positioning_front === false ? 'relative' : 'auto'
                    }
                    onChange={(e) => {
                      const val = e.target.value as 'auto' | 'absolute' | 'relative';
                      let next: boolean | null | undefined = undefined;
                      if (val === 'absolute') next = true;
                      else if (val === 'relative') next = false;
                      else next = undefined;
                      handleFieldChange('absolute_positioning_front', next);
                    }}
                  >
                    <MenuItem value="auto">Auto</MenuItem>
                    <MenuItem value="absolute">Absolute</MenuItem>
                    <MenuItem value="relative">Relative</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="abs-pos-side-label">Side Positioning</InputLabel>
                  <Select
                    labelId="abs-pos-side-label"
                    label="Side Positioning"
                    value={
                      formData.absolute_positioning_side === true ? 'absolute' :
                      formData.absolute_positioning_side === false ? 'relative' : 'auto'
                    }
                    onChange={(e) => {
                      const val = e.target.value as 'auto' | 'absolute' | 'relative';
                      let next: boolean | null | undefined = undefined;
                      if (val === 'absolute') next = true;
                      else if (val === 'relative') next = false;
                      else next = undefined;
                      handleFieldChange('absolute_positioning_side', next);
                    }}
                  >
                    <MenuItem value="auto">Auto</MenuItem>
                    <MenuItem value="absolute">Absolute</MenuItem>
                    <MenuItem value="relative">Relative</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* SVG Upload Section */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            SVG Graphics
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={3}>
              {/* Front SVG Upload */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Front View SVG
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <input
                        accept=".svg,image/svg+xml"
                        style={{ display: 'none' }}
                        id="front-svg-upload"
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          handleSvgFileChange('front', file);
                        }}
                      />
                      <label htmlFor="front-svg-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          fullWidth
                        >
                          {formData.front_svg_file ? 'Change Front SVG' : 'Upload Front SVG'}
                        </Button>
                      </label>
                    </Box>

                    {formData.front_svg_file && (
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={formData.front_svg_file.name}
                          onDelete={() => handleSvgFileChange('front', null)}
                          deleteIcon={<DeleteIcon />}
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    )}

                    {svgPreviews.front && (
                      <Box
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          p: 1,
                          backgroundColor: '#f8f9fa',
                          minHeight: 120,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: svgPreviews.front.includes('<svg')
                              ? svgPreviews.front
                              : `<svg viewBox="-200 -100 800 600" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">${svgPreviews.front}</svg>`
                          }}
                          style={{ width: '100%', height: '100%', maxHeight: '120px' }}
                        />
                      </Box>
                    )}

                    {errors.front_svg_file && (
                      <Typography variant="caption" color="error">
                        {errors.front_svg_file}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Side SVG Upload */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Side View SVG
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <input
                        accept=".svg,image/svg+xml"
                        style={{ display: 'none' }}
                        id="side-svg-upload"
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          handleSvgFileChange('side', file);
                        }}
                      />
                      <label htmlFor="side-svg-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          fullWidth
                        >
                          {formData.side_svg_file ? 'Change Side SVG' : 'Upload Side SVG'}
                        </Button>
                      </label>
                    </Box>

                    {formData.side_svg_file && (
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={formData.side_svg_file.name}
                          onDelete={() => handleSvgFileChange('side', null)}
                          deleteIcon={<DeleteIcon />}
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    )}

                    {svgPreviews.side && (
                      <Box
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          p: 1,
                          backgroundColor: '#f8f9fa',
                          minHeight: 120,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: svgPreviews.side.includes('<svg')
                              ? svgPreviews.side
                              : `<svg viewBox="-200 -100 800 600" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">${svgPreviews.side}</svg>`
                          }}
                          style={{ width: '100%', height: '100%', maxHeight: '120px' }}
                        />
                      </Box>
                    )}

                    {errors.side_svg_file && (
                      <Typography variant="caption" color="error">
                        {errors.side_svg_file}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>SVG Upload Guidelines:</strong>
                    <br />
                    • Upload SVG files for both front and side views of the pattern
                    • Files should be less than 1MB in size
                    • SVG content will be processed and stored in the database
                    • Preview shows how the pattern will appear in the library
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Preview Information */}
        {selectedElementType && (
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Element Type:</strong> {selectedElementType.name}
                {selectedElementType.description && (
                  <>
                    <br />
                    <strong>Description:</strong> {selectedElementType.description}
                  </>
                )}
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Form Actions */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={<SaveIcon />}
            >
              {loading ? 'Saving...' : pattern ? 'Update Pattern' : 'Create Pattern'}
            </Button>
          </Box>
        </Grid>

        {/* Synonym Management - Only show when editing existing pattern */}
        {pattern && (
          <Grid item xs={12}>
            <SynonymManager
              entityType="pattern"
              entityId={pattern.id}
              entityName={pattern.description}
              disabled={loading}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PatternForm;
